// Compact file previews for a grid cell with a server-rendered popover.
//
// The field value is a JSON array of file labels. The matching HTML slot
// supplies the popover markup (for example, `documents` uses
// `__documentsHtml`), keeping links and actions server-rendered.

const SVG_NS = "http://www.w3.org/2000/svg"

function documentIcon() {
  const svg = document.createElementNS(SVG_NS, "svg")
  svg.setAttribute("viewBox", "0 0 20 20")
  svg.setAttribute("fill", "currentColor")
  svg.setAttribute("aria-hidden", "true")
  svg.setAttribute("class", "w-4 h-4 text-primary")

  const path = document.createElementNS(SVG_NS, "path")
  path.setAttribute("d", "M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.62a1.5 1.5 0 0 0-.44-1.06l-4.12-4.12A1.5 1.5 0 0 0 11.38 2H4.5Zm7 .75 4.75 4.75H12a.5.5 0 0 1-.5-.5V2.75Z")
  svg.appendChild(path)
  return svg
}

function compactLabel(label) {
  return label.length > 12 ? `${label.slice(0, 9)}...` : label
}

function parseLabels(value) {
  if (!value) return []

  try {
    const labels = typeof value === "string" ? JSON.parse(value) : value
    return Array.isArray(labels) ? labels : []
  } catch {
    return []
  }
}

function popoverFrom(html) {
  if (!html) return null

  const template = document.createElement("template")
  template.innerHTML = html.trim()
  return template.content.querySelector("[popover]")
}

class FileListCellRenderer {
  init(params) {
    this.params = params
    this.gui = document.createElement("div")
    this.gui.className = "relative flex h-full w-full items-center overflow-hidden"
    this.render()
  }

  getGui() {
    return this.gui
  }

  refresh(params) {
    this.params = params
    this.render()
    return true
  }

  destroy() {
    this.resizeObserver?.disconnect()
    if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame)
  }

  render() {
    this.resizeObserver?.disconnect()
    if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame)
    this.gui.replaceChildren()
    delete this.gui.dataset.controller
    delete this.gui.dataset.action
    this.previews = []

    if (this.params.node?.rowPinned) return

    const labels = parseLabels(this.params.value)
    const field = this.params.colDef.field
    const popover = popoverFrom(this.params.data?.[`__${field}Html`])
    if (!popover) return

    this.gui.dataset.controller = "interactive-popover"
    this.gui.dataset.action = "pointerenter->interactive-popover#show pointerleave->interactive-popover#scheduleHide focusin->interactive-popover#show focusout->interactive-popover#scheduleHide"
    this.gui.appendChild(this.buildCellTrigger(popover.id))

    this.previewContainer = document.createElement("div")
    this.previewContainer.className = "pointer-events-none relative z-10 flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
    this.gui.appendChild(this.previewContainer)

    if (labels.length > 0) {
      labels.forEach((label) => this.addPreview(label))
      this.overflowBadge = document.createElement("span")
      this.overflowBadge.className = "badge badge-ghost badge-sm shrink-0 text-xs"
      this.overflowBadge.hidden = true
      this.previewContainer.appendChild(this.overflowBadge)
    } else {
      this.overflowBadge = null
      const emptyLabel = document.createElement("span")
      emptyLabel.className = "text-xs text-base-content/45"
      emptyLabel.textContent = "No files"
      this.previewContainer.appendChild(emptyLabel)
    }

    this.gui.appendChild(popover)

    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout())
    this.resizeObserver.observe(this.gui)
    this.scheduleLayout()
  }

  addPreview(labelText) {
    const preview = document.createElement("span")
    preview.title = labelText
    preview.className = "inline-flex h-5 max-w-28 shrink-0 items-center gap-0.5 text-xs text-base-content"
    preview.appendChild(documentIcon())

    const label = document.createElement("span")
    label.className = "truncate"
    label.textContent = compactLabel(labelText)
    preview.appendChild(label)

    this.previews.push(preview)
    this.previewContainer.appendChild(preview)
  }

  buildCellTrigger(popoverId) {
    const trigger = document.createElement("button")
    trigger.type = "button"
    trigger.className = "absolute inset-0 z-0 cursor-pointer"
    trigger.setAttribute("aria-label", "Manage files")
    trigger.setAttribute("popovertarget", popoverId)
    trigger.dataset.interactivePopoverTarget = "trigger"
    trigger.dataset.action = "click->interactive-popover#activate"
    return trigger
  }

  scheduleLayout() {
    if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame)
    this.layoutFrame = requestAnimationFrame(() => this.layoutPreviews())
  }

  layoutPreviews() {
    this.layoutFrame = null
    if (this.previews.length === 0) return

    const availableWidth = this.previewContainer.clientWidth
    const gap = 4

    this.previews.forEach((preview) => { preview.hidden = false })
    this.overflowBadge.hidden = true

    const widths = this.previews.map((preview) => preview.offsetWidth)
    const totalWidth = widths.reduce((sum, width) => sum + width, 0) + gap * (widths.length - 1)

    if (totalWidth <= availableWidth) return

    this.overflowBadge.textContent = `+${this.previews.length}`
    this.overflowBadge.hidden = false

    const fittingCount = (badgeWidth) => {
      let usedWidth = 0
      let count = 0

      for (const width of widths) {
        const previewWidth = count === 0 ? width : usedWidth + gap + width
        if (previewWidth + gap + badgeWidth > availableWidth) break
        usedWidth = previewWidth
        count++
      }

      return count
    }

    let visibleCount = fittingCount(this.overflowBadge.offsetWidth)
    this.overflowBadge.textContent = `+${this.previews.length - visibleCount}`
    visibleCount = fittingCount(this.overflowBadge.offsetWidth)
    this.overflowBadge.textContent = `+${this.previews.length - visibleCount}`

    this.previews.forEach((preview, index) => {
      preview.hidden = index >= visibleCount
    })
  }
}

export const fileList = {
  apply() {
    return {
      cellRenderer: FileListCellRenderer,
      editable: false
    }
  }
}
